<script>
	import { onMount, afterUpdate } from 'svelte';
	import { fetchConceptTree, fetchNodeContributions } from '../lib/api.js';
	import OrientationBlock from '../components/OrientationBlock.svelte';

	export let nodeId;
	export let navigate;

	let allNodes = [];
	let loading = true;
	let error = null;
	let contributions = [];
	let contributionsLoading = false;

	// ╔═══════════════════════════════════════════════════════════════════════╗
	// ║ LINKED EXPECTATION — must stay in lockstep with ExplorerTree.svelte    ║
	// ║                                                                        ║
	// ║ This file is the "open full" / new-tab variant of the same concept     ║
	// ║ rendering. A user must see the SAME tree structure, the SAME flatten   ║
	// ║ semantics (trunk → branch → leaves), the SAME generality formatting,   ║
	// ║ tier classes, and domain shortening as the in-page Explorer.           ║
	// ║                                                                        ║
	// ║ When you change leavesOf, childrenOf, formatGenerality, tierClass,     ║
	// ║ tierLabel, or shortDomain here, you MUST mirror the change in          ║
	// ║ /src/routes/ExplorerTree.svelte (and vice versa).                      ║
	// ╚═══════════════════════════════════════════════════════════════════════╝
	function childrenOf(parentId) {
		return allNodes.filter(n => n.parent_id === parentId);
	}

	// Mirror of ExplorerTree.collectDescendants — BFS over allNodes returning
	// the node id Set including the input id.
	function collectDescendants(nodeId) {
		const ids = new Set();
		ids.add(nodeId);
		const queue = [nodeId];
		while (queue.length) {
			const pid = queue.shift();
			for (const n of allNodes) {
				if (n.parent_id === pid && !ids.has(n.id)) {
					ids.add(n.id);
					queue.push(n.id);
				}
			}
		}
		return ids;
	}

	// Mirror of ExplorerTree.leavesOf — every descendant of the branch at any
	// substrate depth, flattened and sorted by generality desc. The substrate
	// retains the real hierarchy; this view-layer flatten is what makes a
	// ladder-shaped Cartographer skeleton readable.
	function leavesOf(branchId) {
		const ids = collectDescendants(branchId);
		ids.delete(branchId);
		return [...ids]
			.map(id => allNodes.find(n => n.id === id))
			.filter(Boolean)
			.sort((a, b) => (b.generality ?? 0) - (a.generality ?? 0));
	}

	// Render markdown via the global `marked` library loaded in index.html.
	// Falls back to escaped plain text if marked isn't available (defensive).
	// Content is from authenticated tenants, not anonymous input — XSS risk
	// is medium; revisit if we open user-supplied markdown to public auth.
	function renderMarkdown(text) {
		if (text == null) return '';
		const s = String(text);
		// Marked v11 UMD exports the parse function as window.marked itself
		// (callable), NOT an object with .parse. Earlier feature-detection
		// for .parse silently failed. Try both API shapes.
		if (typeof window !== 'undefined' && window.marked) {
			try {
				if (typeof window.marked === 'function') return window.marked(s);
				if (typeof window.marked.parse === 'function') return window.marked.parse(s);
			} catch { /* fall through to plain */ }
		}
		return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
	}

	function formatGenerality(g) {
		return '.' + ((g || 0) * 100).toFixed(0).padStart(2, '0');
	}

	function tierClass(g) {
		if (g >= 0.65) return 'ct-tier-trunk';
		if (g >= 0.35) return 'ct-tier-branch';
		return 'ct-tier-leaf';
	}

	function tierLabel(g) {
		if (g >= 0.65) return 'trunk';
		if (g >= 0.35) return 'branch';
		return 'leaf';
	}

	function shortDomain(d) {
		const parts = (d || '').split('/');
		return parts.length > 1 ? parts[parts.length - 1] : (d || '').split('-').slice(0, 3).join(' ');
	}

	// Title-case version used when the domain is promoted to a headline.
	function titleDomain(d) {
		return shortDomain(d)
			.split(' ')
			.map(w => (w ? w[0].toUpperCase() + w.slice(1) : w))
			.join(' ');
	}

	// Derived
	$: rootNode = allNodes.find(n => n.id === nodeId) || null;
	$: children = rootNode
		? childrenOf(rootNode.id).sort((a, b) => (b.generality ?? 0) - (a.generality ?? 0))
		: [];

	// Count all descendants
	function descendantCount(id) {
		const direct = childrenOf(id);
		let total = direct.length;
		for (const c of direct) total += descendantCount(c.id);
		return total;
	}

	$: totalDescendants = rootNode ? descendantCount(rootNode.id) : 0;

	// Concept name is the headline. Domain is a categorical tag in the meta row.
	// (Prior behavior promoted the primary domain to the title and demoted
	// concept to subtitle — a workaround for earlier ingests that produced
	// sloppy concept names. The turn-3 specificity + humanize-machine-identifier
	// rules now produce strong concept names, so the swap is no longer needed.)
	$: primaryDomain = rootNode?.domains?.[0] || null;
	$: rootTitle = rootNode ? rootNode.concept : '';

	// Split context into lead sentence + rest
	function splitLead(text) {
		if (!text) return { lead: '', rest: '' };
		const match = text.match(/^[^.!?]*[.!?]/);
		if (!match) return { lead: text, rest: '' };
		const lead = match[0];
		const rest = text.substring(lead.length).trim();
		return { lead, rest };
	}

	function cleanMermaid(code) {
		if (!code) return '';
		return code
			.replace(/%%\{init:.*?}%%\n?/s, '')
			.replace(/\n\s*classDef default[^\n]*/g, '')
			// Pipeline diagrammer bakes `font-size:17px` into every classDef.
			// Strip it here so the global Mermaid initialize() + our CSS rules
			// (13px body-parity) take effect on nodes too, not just edge labels.
			.replace(/,?\s*font-size\s*:\s*\d+(?:\.\d+)?\s*(px|pt|em|rem)?/gi, '')
			.trim();
	}

	function leafValue(leaf) {
		const c = leaf.content;
		if (!c || typeof c !== 'object') return '';
		return Object.values(c).filter(v => typeof v === 'string' && v.trim()).join('\n\n');
	}

	afterUpdate(() => {
		if (!window.mermaid) return;
		// Reset data-processed on any .mermaid element without a rendered
		// svg. Svelte reactivity swaps text inside <pre class="mermaid">
		// when we navigate between concepts, but the element retains
		// data-processed="true" from the previous diagram. Mermaid then
		// skips it, leaving new text as raw code. This reset forces
		// mermaid.run() to re-process anything that isn't currently a svg.
		const pending = [];
		document.querySelectorAll('.cf-diagram .mermaid').forEach(el => {
			if (!el.querySelector('svg')) {
				el.removeAttribute('data-processed');
				pending.push(el);
			}
		});
		// Only hide diagrams that affirmatively rendered as errors. "No svg"
		// means mermaid hasn't finished (or Svelte re-mounted the element) —
		// leave alone instead of hiding every diagram.
		const hideFailed = () => {
			document.querySelectorAll('.cf-diagram').forEach(el => {
				const svg = el.querySelector('svg');
				if (!svg) return;
				const hasErrorClass =
					svg.getAttribute('aria-roledescription') === 'error' ||
					svg.classList.contains('flowchart-error') ||
					svg.classList.contains('error') ||
					el.querySelector('.error-icon');
				if (hasErrorClass) {
					el.style.display = 'none';
					el.setAttribute('data-mermaid-failed', 'true');
				}
			});
		};
		const run = pending.length > 0
			? window.mermaid.run({ nodes: pending, suppressErrors: true })
			: window.mermaid.run({ suppressErrors: true });
		(run && typeof run.then === 'function') ? run.then(hideFailed).catch(hideFailed) : setTimeout(hideFailed, 150);
	});

	async function loadTree() {
		try {
			loading = true;
			error = null;
			const data = await fetchConceptTree();
			allNodes = (data.nodes || []).map(n => ({
				...n,
				generality: typeof n.generality === 'string' ? parseFloat(n.generality) : n.generality
			}));
			if (!allNodes.find(n => n.id === nodeId)) {
				error = 'Concept not found';
			}
		} catch (err) {
			console.error('Failed to load concept:', err);
			error = err?.message || 'Failed to load concept';
		} finally {
			loading = false;
		}
	}

	// Contribution log — tinted per-row by authored_by_type (human / agent /
	// system). Root node only; children keep their existing metadata blocks.
	// Captures nodeId at call time so a fast second navigation doesn't let an
	// in-flight fetch overwrite the newer node's rows.
	async function loadContributions(targetNodeId) {
		contributions = [];
		contributionsLoading = true;
		try {
			const data = await fetchNodeContributions(targetNodeId);
			if (targetNodeId !== nodeId) return; // navigated away — drop the result
			contributions = data.contributions || [];
		} catch (err) {
			console.warn('Failed to load contributions:', err);
			if (targetNodeId === nodeId) contributions = [];
		} finally {
			if (targetNodeId === nodeId) contributionsLoading = false;
		}
	}

	function contributionClass(c) {
		const a = (c.authored_by_type || 'unknown').toLowerCase();
		if (a === 'agent') return 'cf-contrib-agent';
		if (a === 'system') return 'cf-contrib-system';
		if (a === 'human') return 'cf-contrib-human';
		return 'cf-contrib-unknown';
	}

	function formatContribDate(iso) {
		if (!iso) return '';
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
	}

	// Header timestamp on a concept — "Last updated Apr 26, 2026 at 14:32".
	// Distinct from formatContribDate (no time) because the per-contribution
	// stripe doesn't need clock precision; the concept header does, since
	// re-ingests within a day are common and the date alone wouldn't tell
	// the reader whether they're looking at a stale view.
	function formatUpdatedAt(iso) {
		if (!iso) return '';
		const d = new Date(iso);
		if (isNaN(d.getTime())) return iso;
		const datePart = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
		const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
		return `${datePart} at ${timePart}`;
	}

	function backToExplorer() {
		if (typeof navigate === 'function') {
			navigate('/explorer');
		} else {
			window.location.pathname = '/explorer';
		}
	}

	onMount(() => {
		loadTree();
	});

	// Re-fire on nodeId change so navigating between concepts refreshes the log.
	// loadTree stays mount-only — the full tree is tenant-scoped, not per-node.
	$: if (nodeId) loadContributions(nodeId);
</script>

<svelte:head>
	<title>{rootNode ? rootTitle + ' · ADRASI' : 'Concept · ADRASI'}</title>
</svelte:head>

<div class="cf-page">
	<header class="cf-header">
		<button class="cf-back" on:click={backToExplorer} title="Back to concept tree">← Concept tree</button>
		<span class="cf-brand">ADRASI</span>
	</header>

	{#if loading}
		<div class="cf-state">Loading concept...</div>
	{:else if error}
		<div class="cf-state cf-error">{error}</div>
	{:else if !rootNode}
		<div class="cf-state">Concept not found.</div>
	{:else}
		<article class="cf-reader">
			<!-- ── ROOT ── -->
			<section class="cf-root">
				<div class="cf-meta-row">
					<span class="cf-generality {tierClass(rootNode.generality)}">{formatGenerality(rootNode.generality)}</span>
					<span class="cf-tier-label">{tierLabel(rootNode.generality)}</span>
					{#if rootNode.domains && rootNode.domains.length > 0}
						<span class="cf-domains">
							{#each rootNode.domains as d}<span class="cf-domain-tag">{shortDomain(d)}</span>{/each}
						</span>
					{/if}
				</div>
				<h1 class="cf-root-title">{rootTitle}</h1>
				{#if rootNode.updated_at}
					<div class="cf-updated-at">Last updated {formatUpdatedAt(rootNode.updated_at)}</div>
				{/if}
				{#if rootNode.context}
					{@const split = splitLead(rootNode.context)}
					<p class="cf-root-context">
						<span class="cf-root-lead">{split.lead}</span>
						{#if split.rest}&nbsp;<span class="cf-root-rest">{split.rest}</span>{/if}
					</p>
				{/if}

				<!-- ── AT-A-GLANCE (orientation) ── -->
				<OrientationBlock orientation={rootNode.content?.orientation} />

				<!-- Diagram sits right after the summary / orientation block,
				     before the body content + TOC. One per concept. -->
				{#if rootNode.diagram?.mermaid_code}
					<div class="cf-diagram"><div class="mermaid">{cleanMermaid(rootNode.diagram.mermaid_code)}</div></div>
				{/if}

				{#if rootNode.content && typeof rootNode.content === 'object'}
					{#each Object.entries(rootNode.content).filter(([k, v]) => k !== 'orientation' && typeof v === 'string') as [domain, text]}
						{#if text}
							<div class="cf-content-block">
								<div class="cf-content-domain">{shortDomain(domain)}</div>
								<div class="cf-content-text">{@html renderMarkdown(text)}</div>
							</div>
						{/if}
					{/each}
				{/if}
				{#if rootNode.entities?.length}
					<div class="cf-entities">
						{#each rootNode.entities as e}<span class="cf-entity-tag">{e}</span>{/each}
					</div>
				{/if}
				{#if rootNode.source_files?.length}
					<div class="cf-sources">
						<div class="cf-sources-label">Sources — {rootNode.source_files.length}</div>
						{#each rootNode.source_files as sf}
							<div class="cf-source-item">{sf.filename || sf.domain || 'unknown'}</div>
						{/each}
					</div>
				{/if}
				{#if contributions.length > 0}
					<div class="cf-contributions">
						<div class="cf-contributions-label">Contribution log — {contributions.length}</div>
						<div class="cf-contrib-rows">
							{#each contributions as c (c.id)}
								<div class="cf-contrib-row {contributionClass(c)}">
									<span class="cf-contrib-author">{c.authored_by_type}</span>
									<span class="cf-contrib-source">{c.source_filename || '—'}</span>
									<span class="cf-contrib-type">{c.contribution_type}</span>
									<span class="cf-contrib-date">{formatContribDate(c.contributed_at)}</span>
								</div>
							{/each}
						</div>
					</div>
				{/if}
				<div class="cf-stats">
					<span class="cf-stat"><strong>{children.length}</strong> direct children</span>
					<span class="cf-stat-sep">·</span>
					<span class="cf-stat"><strong>{totalDescendants}</strong> total nodes</span>
				</div>
			</section>

			<!-- ── CHILDREN (branches) — leaves below are flattened across substrate depth ── -->
			{#each children as child (child.id)}
				{@const grandchildren = leavesOf(child.id)}
				<section class="cf-child">
					<header class="cf-child-head">
						<span class="cf-generality {tierClass(child.generality)}">{formatGenerality(child.generality)}</span>
						<h2 class="cf-child-title">{child.concept}</h2>
					</header>
					{#if child.context}
						<p class="cf-child-context">{child.context}</p>
					{/if}

					<!-- Diagram sits right after the child's summary line, before
					     the body content + leaves TOC. One per concept. -->
					{#if child.diagram?.mermaid_code}
						<div class="cf-diagram"><div class="mermaid">{cleanMermaid(child.diagram.mermaid_code)}</div></div>
					{/if}

					{#if child.content && typeof child.content === 'object'}
						{#each Object.entries(child.content).filter(([k, v]) => k !== 'orientation' && typeof v === 'string') as [domain, text]}
							{#if text}
								<div class="cf-content-block cf-content-block-small">
									<div class="cf-content-domain">{shortDomain(domain)}</div>
									<div class="cf-content-text">{@html renderMarkdown(text)}</div>
								</div>
							{/if}
						{/each}
					{/if}
					{#if child.entities?.length}
						<div class="cf-entities cf-entities-small">
							{#each child.entities as e}<span class="cf-entity-tag">{e}</span>{/each}
						</div>
					{/if}

					<!-- ── GRANDCHILDREN (leaves) ── -->
					{#if grandchildren.length > 0}
						<ul class="cf-grandchildren">
							{#each grandchildren as leaf (leaf.id)}
								<li class="cf-leaf">
									<div class="cf-leaf-row">
										<span class="cf-generality cf-generality-small {tierClass(leaf.generality)}">{formatGenerality(leaf.generality)}</span>
										<span class="cf-leaf-title">{leaf.concept}</span>
									</div>
									{#if leaf.context}
										<p class="cf-leaf-context">{leaf.context}</p>
									{/if}
									{#if leafValue(leaf)}
										<div class="cf-leaf-value">{leafValue(leaf)}</div>
									{/if}
								</li>
							{/each}
						</ul>
					{/if}
				</section>
			{/each}

			{#if children.length === 0}
				<section class="cf-empty">No child concepts yet.</section>
			{/if}
		</article>
	{/if}
</div>

<style>
	.cf-page {
		min-height: 100vh;
		background: #F5F6FA;
		color: #1A1A1A;
		font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
		padding: 0 0 80px;
	}

	.cf-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: 48px;
		padding: 0 24px;
		background: #FFFFFF;
		border-bottom: 1px solid #E5E7EB;
		position: sticky;
		top: 0;
		z-index: 10;
	}

	.cf-back {
		background: none;
		border: none;
		font-size: 12px;
		color: #6B7280;
		cursor: pointer;
		padding: 6px 10px;
		border-radius: 4px;
		font-family: inherit;
		transition: all 120ms ease-out;
	}
	.cf-back:hover { color: #111827; background: #F3F4F6; }

	.cf-brand {
		font-size: 11px;
		font-weight: 700;
		letter-spacing: 0.12em;
		color: #9CA3AF;
		text-transform: uppercase;
	}

	.cf-state {
		padding: 120px 24px;
		text-align: center;
		font-size: 14px;
		color: #6B7280;
	}
	.cf-error { color: #B91C1C; }

	.cf-reader {
		max-width: 760px;
		margin: 0 auto;
		padding: 56px 32px 0;
	}

	/* ── Root ── */
	.cf-root { margin-bottom: 56px; }

	.cf-meta-row {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 18px;
		flex-wrap: wrap;
	}

	.cf-generality {
		font-size: 11px;
		font-weight: 700;
		font-variant-numeric: tabular-nums;
		color: #6B7280;
	}
	.cf-generality-small { font-size: 10px; font-weight: 600; }
	.ct-tier-trunk { color: #6B7280; }
	.ct-tier-branch { color: #9CA3AF; }
	.ct-tier-leaf { color: #C4C9D4; }

	.cf-tier-label {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: #9CA3AF;
	}

	.cf-domains { display: flex; gap: 5px; flex-wrap: wrap; margin-left: 4px; }
	.cf-domain-tag {
		font-size: 10px;
		font-weight: 500;
		color: #6B7280;
		background: #ECEEF3;
		padding: 2px 8px;
		border-radius: 3px;
	}

	.cf-root-title {
		font-size: 36px;
		font-weight: 700;
		line-height: 1.15;
		color: #111827;
		margin: 0 0 6px;
		letter-spacing: -0.02em;
	}

	.cf-root-subtitle {
		font-size: 15px;
		font-weight: 500;
		color: #6B7280;
		margin: 0 0 22px;
		letter-spacing: -0.005em;
	}

	.cf-updated-at {
		font-size: 11.5px;
		font-weight: 500;
		letter-spacing: 0.02em;
		color: #9CA3AF;
		margin: 4px 0 22px;
	}

	.cf-root-context {
		font-size: 17px;
		line-height: 1.6;
		color: #374151;
		margin: 0 0 28px;
	}
	.cf-root-lead { font-weight: 600; color: #111827; }
	.cf-root-rest { color: #4B5563; }

	/* Orientation styles now live in components/OrientationBlock.svelte. */

	.cf-content-block {
		padding: 14px 18px;
		margin: 18px 0;
		background: #FFFFFF;
		border: 1px solid #E5E7EB;
		border-radius: 6px;
	}
	.cf-content-block-small {
		padding: 10px 14px;
		margin: 10px 0;
	}
	.cf-content-domain {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: #9CA3AF;
		margin-bottom: 6px;
	}
	.cf-content-text {
		font-size: 13px;
		line-height: 1.7;
		color: #374151;
	}
	/* Markdown rendered into content-text — minimal typographic hygiene so
	   bold/italic/code/lists/headings don't read as wall-of-text. Scoped to
	   the content-text container; doesn't affect anything else. */
	.cf-content-text :global(p) { margin: 0 0 8px; }
	.cf-content-text :global(p:last-child) { margin-bottom: 0; }
	.cf-content-text :global(strong) { font-weight: 600; color: #1F2937; }
	.cf-content-text :global(em) { font-style: italic; color: #1F2937; }
	.cf-content-text :global(code) { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; background: #F3F4F6; padding: 1px 5px; border-radius: 3px; color: #1F2937; }
	.cf-content-text :global(pre) { background: #F3F4F6; padding: 10px 12px; border-radius: 5px; overflow-x: auto; font-size: 12px; margin: 8px 0; }
	.cf-content-text :global(pre code) { background: transparent; padding: 0; }
	.cf-content-text :global(ul), .cf-content-text :global(ol) { margin: 4px 0 8px; padding-left: 22px; }
	.cf-content-text :global(li) { margin: 2px 0; }
	.cf-content-text :global(h1), .cf-content-text :global(h2), .cf-content-text :global(h3) { font-size: 13px; font-weight: 600; color: #1F2937; margin: 10px 0 4px; }
	.cf-content-text :global(blockquote) { border-left: 2px solid #E5E7EB; margin: 6px 0; padding: 0 0 0 10px; color: #4B5563; font-style: italic; }
	.cf-content-text :global(a) { color: #1B3A5C; text-decoration: underline; }

	.cf-entities {
		display: flex;
		flex-wrap: wrap;
		gap: 5px;
		margin: 14px 0 20px;
	}
	.cf-entities-small { margin: 10px 0 14px; }
	.cf-entity-tag {
		font-size: 10px;
		color: #6B7280;
		background: #F3F4F6;
		padding: 3px 9px;
		border-radius: 10px;
	}

	.cf-sources {
		margin-top: 24px;
		padding-top: 18px;
		border-top: 1px solid #E5E7EB;
	}
	.cf-sources-label {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.07em;
		color: #9CA3AF;
		margin-bottom: 6px;
	}
	.cf-source-item {
		font-size: 11px;
		color: #6B7280;
		padding: 2px 0;
	}

	/* ── Contribution log (in-doc striping by author) ──
	   Tint matches ExplorerTree: ivory #FBFAF6 = human, cold #F6F7FB = agent,
	   neutral #F7F7F6 = system. Very subtle; the row should read as one sheet
	   of slightly-different paper, not a coloured tag. */
	.cf-contributions {
		margin-top: 24px;
		padding-top: 18px;
		border-top: 1px solid #E5E7EB;
	}
	.cf-contributions-label {
		font-size: 10px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.07em;
		color: #9CA3AF;
		margin-bottom: 8px;
	}
	.cf-contrib-rows {
		display: flex;
		flex-direction: column;
	}
	.cf-contrib-row {
		display: grid;
		grid-template-columns: 56px 1fr 80px 90px;
		align-items: center;
		gap: 12px;
		padding: 6px 10px;
		font-size: 11px;
		color: #4B5563;
		border-top: 1px solid #F3F4F6;
	}
	.cf-contrib-rows > .cf-contrib-row:first-child { border-top: none; }
	@media (max-width: 680px) {
		.cf-contrib-row {
			grid-template-columns: 48px 1fr 72px;
			gap: 8px;
			padding: 6px 8px;
		}
		.cf-contrib-type { display: none; }
	}
	.cf-contrib-human { background: #FBFAF6; }
	.cf-contrib-agent { background: #F6F7FB; }
	.cf-contrib-system { background: #F7F7F6; }
	.cf-contrib-unknown { background: transparent; }
	.cf-contrib-author {
		font-size: 9px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.08em;
		color: #6B7280;
	}
	.cf-contrib-human .cf-contrib-author { color: #8A7A4D; }
	.cf-contrib-agent .cf-contrib-author { color: #1B3A5C; }
	.cf-contrib-system .cf-contrib-author { color: #5F6368; }
	.cf-contrib-source {
		color: #374151;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.cf-contrib-type { color: #9CA3AF; }
	.cf-contrib-date { color: #9CA3AF; text-align: right; }

	.cf-stats {
		display: flex;
		align-items: center;
		gap: 10px;
		margin-top: 28px;
		padding-top: 18px;
		border-top: 1px solid #E5E7EB;
		font-size: 11px;
		color: #9CA3AF;
	}
	.cf-stats strong { color: #4B5563; font-weight: 600; }
	.cf-stat-sep { color: #D1D5DB; }

	/* ── Child ── */
	.cf-child {
		padding: 28px 0;
		border-top: 1px solid #E5E7EB;
	}
	.cf-child-head {
		display: flex;
		align-items: baseline;
		gap: 12px;
		margin-bottom: 12px;
	}
	.cf-child-title {
		font-size: 20px;
		font-weight: 600;
		line-height: 1.3;
		color: #111827;
		margin: 0;
		letter-spacing: -0.01em;
	}
	.cf-child-context {
		font-size: 14px;
		line-height: 1.7;
		color: #4B5563;
		margin: 0 0 14px;
	}

	/* ── Grandchildren (leaves) ── */
	.cf-grandchildren {
		list-style: none;
		padding: 0;
		margin: 18px 0 0;
		border-top: 1px solid #F3F4F6;
	}
	.cf-leaf {
		padding: 12px 0;
		border-bottom: 1px solid #F3F4F6;
	}
	.cf-leaf:last-child { border-bottom: none; }
	.cf-leaf-row {
		display: flex;
		align-items: baseline;
		gap: 10px;
		margin-bottom: 4px;
	}
	.cf-leaf-title {
		font-size: 13px;
		font-weight: 600;
		color: #374151;
	}
	.cf-leaf-context {
		font-size: 12.5px;
		line-height: 1.65;
		color: #6B7280;
		margin: 0;
		padding-left: 30px;
	}

	.cf-leaf-value {
		font-size: 12px;
		line-height: 1.65;
		color: #6B7280;
		margin: 2px 0 0;
		padding-left: 30px;
		white-space: pre-wrap;
	}

	.cf-diagram {
		padding: 24px 14px 20px;
		margin: 14px 0;
		/* Mermaid may set hardcoded width on its SVG — allow horizontal scroll
		   rather than blowing out the page layout on mobile. */
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
	}
	.cf-diagram :global(.mermaid) { display: flex; justify-content: center; }
	/* Match lane-2 sizing convention: cap the SVG display size so diagrams
	   read as embedded figures. 640px in the full view since the column is
	   wider than lane 2. */
	.cf-diagram :global(svg) { max-width: 640px; width: 100%; height: auto; margin: 0 auto; display: block; }
	/* Neutralize Mermaid's per-class font-size overrides so the global 12px
	   from index.html mermaid.initialize() wins. Keeps diagram labels at
	   parity with surrounding body text instead of looming over it. */
	.cf-diagram :global(svg text),
	.cf-diagram :global(svg .nodeLabel),
	.cf-diagram :global(svg .label) { font-size: 11.5px !important; }

	/* ── Empty ── */
	.cf-empty {
		padding: 40px 0;
		text-align: center;
		font-size: 12px;
		color: #9CA3AF;
		border-top: 1px solid #E5E7EB;
	}

	/* ── Responsive ── */
	@media (max-width: 680px) {
		.cf-reader { padding: 32px 20px 0; }
		.cf-root-title { font-size: 28px; }
		.cf-root-context { font-size: 15px; }
		.cf-child-title { font-size: 17px; }
	}
</style>
